import { Button, Checkbox, TextField, Typography, FormControlLabel, Box, Stack } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { basicFont, carInfoFont } from "../../../../shared/styles/fontStyles";
import { defaultTextFieldStyle } from "../../../../shared/styles/textfieldStyles";
import { altChooseButtonStyleTurquoise } from "../../../../shared/styles/buttonStyles";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { submitReservation } from "../../../../app/store/reservationSlice";
import '../../../../shared/styles/style.css';
import InfoForConfirmation from "./InfoForConfirmation";
import { useValidateCustomerMutation } from "../../../../app/store/api/customerApi";
import { boxStyleHighlighted } from "../../../../shared/styles/boxStyles";
import { highlLightedTextStyle } from "../../../../shared/styles/typographStyles";
import { useWindowSize } from "../../../../shared/hooks/useWindowSize";

export default function ContactInfo() {
    
    const { t, i18n } = useTranslation();
    const draft = useSelector((state) => state.reservation.draft);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [validateCustomer] = useValidateCustomerMutation();
    const { isMobile } = useWindowSize();
    const [contactInfo, setContactInfo] = useState({
        name: '',
        email: '',
        phone: '',
        note: '',
        emailMarketingOk: false,
        serviceTermsOk: false
    });
    const [ errors, setErrors ] = useState(null);
    const [ disabled, setDisabled ] = useState(true);
    const [touched, setTouched] = useState({});

    // useEffect(() => {
    //     console.log("Draft after submit:", draft);
    // }, [draft]);

    useEffect(() => {
        if (contactInfo.serviceTermsOk && contactInfo.name && contactInfo.email && contactInfo.phone) {
            setDisabled(false);
        } else {
            setDisabled(true);
        }
    }, [contactInfo]);

    const validateField = (name, value) => {
        let error = "";

        switch (name) {
            case "name":
                if (!value.trim()) {
                    error = {
                        en: "Name is required",
                        fi: "Nimi on pakollinen",
                        sv: "Namn är obligatoriskt"
                    };
                } else if (value.trim().length < 2) {
                    error = {
                        en: "Name must be at least 2 characters",
                        fi: "Nimen on oltava vähintään 2 merkkiä",
                        sv: "Namnet måste vara minst 2 tecken"
                    };
                }
                break;

            case "email":
                if (!value.trim()) {
                    error = {
                        en: "Email is required",
                        fi: "Sähköposti on pakollinen",
                        sv: "E-post är obligatoriskt"
                    };
                } else if (
                    !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)
                ) {
                    error = {
                        en: "Enter a valid email address",
                        fi: "Anna voimassa oleva sähköpostiosoite",
                        sv: "Ange en giltig e-postadress"
                    };
                }
                break;

            case "phone":
                if (!value.trim()) {
                    error = {
                        en: "Phone number is required",
                        fi: "Puhelinnumero on pakollinen",
                        sv: "Telefonnummer är obligatoriskt"
                    };
                } else if (value.length < 5 || !/^\d+$/.test(value)) {
                    error = {
                        en: 'Phone is required and must contain only numbers.',
                        fi: 'Puhelinnumeron tulee sisältää vain numeroita.',
                        sv: 'Telefonnumret får endast innehålla siffror.'
                    };
                }
                break;

            default:
                break;
        }

        setErrors((prev) => ({
            ...prev,
            [name]: error,
        }));

        return error === "";
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;

        setTouched((prev) => ({
            ...prev,
            [name]: true,
        }));

        validateField(name, value);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setContactInfo((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (touched[name]) {
            validateField(name, value);
        }
    };

    const handleCheckboxChange = (event) => {
        const { name, checked } = event.target;
        setContactInfo((prev) => ({
            ...prev,
            [name]: checked
        }));
    };

    const submitContactInfo = async (event) => {
        event.preventDefault();
        // First validation can be done here
        // Then if valid, proceed with the submission logic
        const valid = await isValidContactInfo();
        if (!valid) {
            return;
        }
        
        const reservationData = { ...draft, customer: contactInfo };
        const res = await dispatch(submitReservation(reservationData));
        // console.log(res?.payload);
        navigate(`/reservations/${res?.payload?.reservationUuid?.uuid}`, { replace: true });
    }

    const isValidContactInfo = async () => {
        try {
            await validateCustomer(contactInfo).unwrap();
            setErrors(null);

            return true;
        } catch (error) {
            const errorObj = {};

            error.data.errors.forEach((err) => {
                const key = Object.keys(err)[0];
                errorObj[key] = err[key];
            });

            setErrors(errorObj);

            return false;
        }
    };
    
    return (
        <Box className='contact-info'>
            <Typography variant='h5' gutterBottom sx={basicFont} >
                {t('contactInfo.title')}
            </Typography>
            <Box className='contact-info-details'>
                <form onSubmit={submitContactInfo}>
                    <Stack spacing={2} direction={isMobile ? 'column' : 'row'}>
                        <TextField
                            sx={defaultTextFieldStyle}
                            label={t('contactInfo.name')}
                            name="name"
                            variant="outlined"
                            fullWidth
                            required
                            margin="normal"
                            value={contactInfo.name}
                            onChange={(e) => handleInputChange(e)}
                            onBlur={(e) => handleBlur(e)}
                            error={!!errors?.name}
                            helperText={errors?.name ? errors.name[i18n.language] : ''}
                        />
                        <TextField
                            sx={defaultTextFieldStyle}
                            label={t('contactInfo.email')}
                            name="email"
                            variant="outlined"
                            type="email"
                            fullWidth
                            required
                            margin="normal"
                            value={contactInfo.email}
                            onChange={(e) => handleInputChange(e)}
                            onBlur={(e) => handleBlur(e)}
                            error={!!errors?.email}
                            helperText={errors?.email ? errors.email[i18n.language] : ''}
                        />
                    </Stack>
                    <TextField
                        sx={defaultTextFieldStyle}
                        label={t('contactInfo.phone')}
                        name="phone"
                        type="tel"
                        variant="outlined"
                        fullWidth
                        required
                        margin="normal"
                        value={contactInfo.phone}
                        onChange={(e) => handleInputChange(e)}
                        onBlur={(e) => handleBlur(e)}
                        error={!!errors?.phone}
                        helperText={errors?.phone ? errors.phone[i18n.language] : ''}
                    />
                    <TextField
                        sx={defaultTextFieldStyle}
                        label={t('contactInfo.note')}
                        name="note"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        multiline
                        rows={4}
                        value={contactInfo.note}
                        onChange={(e) => handleInputChange(e)}
                    />
                    <Box className='contact-info-checkboxes'>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="emailMarketingOk"
                                    checked={contactInfo.emailMarketingOk}
                                    onChange={handleCheckboxChange}
                                />
                            }
                            label={
                                <Typography variant='body1' component="p" sx={carInfoFont}>
                                    {t('contactInfo.marketingConsent')}
                                </Typography>
                            }
                            labelPlacement="start"
                            sx={{ width: '100%', justifyContent: 'space-between', margin: 0, display: 'flex' }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="serviceTermsOk"
                                    checked={contactInfo.serviceTermsOk}
                                    onChange={handleCheckboxChange}
                                />
                            }
                            label={
                                <Typography variant='body1' component="p" sx={carInfoFont}>
                                    {t('contactInfo.serviceConsent')} *
                                </Typography>
                            }
                            labelPlacement="start"
                            sx={{ width: '100%', justifyContent: 'space-between', margin: 0, display: 'flex' }}
                        />
                    </Box>
                </form>
            </Box>
            <InfoForConfirmation />
            <Box
                sx={{...boxStyleHighlighted}}
            >
                <Typography 
                    variant="body1" 
                    sx={{...highlLightedTextStyle}}
                >
                    {t('actions.confirmInfo')}
                </Typography>
                <Button
                    variant="contained" 
                    sx={{...altChooseButtonStyleTurquoise, width: isMobile ? '100%' : '200px'}}
                    disabled={disabled}
                    onClick={submitContactInfo}
                >
                    {t('actions.confirm')}
                </Button>
            </Box>
        </Box>
    );
}